import React from 'react';
import camelcase from 'camelcase-es5';
import useFormControlReducer from '../../hooks/useFormControlReducer';
import InputLabel from '../InputLabel';
import TextFieldFormControl from './TextFieldFormControl';
import TextFieldInputMultiLine from './TextFieldInputMultiLine';
import TextFieldFooter from './TextFieldFooter';
import TextFieldBase from './TextFieldBase';
import TTextFieldMultiLine from './@types/TextFieldMultiLine';
import generateTextFieldIds, { isCharsExceeded } from './generateTextFieldIds';

const DEFAULT_MIN_ROWS = 2;
const DEFAULT_MAX_ROWS = 10;

/**
 * textarea abstraction
 * currently only supporting external onChange event propagation
 */
const TextFieldMultiLine = ({
  value,
  errorText,
  helperText,
  inputRef,
  isRequired,
  labelText = '',
  onChange,
  onFocus,
  onBlur,
  onMouseOut,
  onMouseOver,
  onKeyPress,
  optionalText,
  charsExceededText,
  charsRemainingText,
  id = `TextFieldMultiLine-${camelcase(labelText)}`,
  maxLength = 0,
  minRows = DEFAULT_MIN_ROWS,
  maxRows = DEFAULT_MAX_ROWS,
  name,
  dataTestId,
  placeholder,
  autoFocus,
  ariaLive,
}: TTextFieldMultiLine) => {
  const { state, handlers } = useFormControlReducer({
    onChange,
    onFocus,
    onBlur,
    onMouseOut,
    onMouseOver,
    errorText,
    value,
  });

  const { isDirty, isEmpty, isErrored, isFocused, characterCount } = state;
  const { handleChange, handleFocus, handleBlur, handleMouseOver, handleMouseOut } = handlers;

  const charsExceeded = isCharsExceeded(characterCount, maxLength);
  const { helperTextId, errorTextId, charsExceededId } = generateTextFieldIds(id, Boolean(errorText), charsExceeded);

  return (
    <TextFieldBase kind="multiline" ariaLive={ariaLive}>
      {labelText && (
        <InputLabel htmlFor={id} isInputRequired={isRequired} labelText={labelText} optionalText={optionalText} />
      )}
      <TextFieldFormControl
        isMultiline
        isDirty={isDirty}
        isErrored={isErrored || charsExceeded}
        isFocused={isFocused}
        isEmpty={isEmpty}
      >
        <TextFieldInputMultiLine
          id={id}
          ref={inputRef}
          isRequired={isRequired}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          onKeyPress={onKeyPress}
          minRows={minRows}
          maxRows={maxRows}
          name={name}
          autoFocus={autoFocus}
          data-testid={dataTestId}
          placeholder={placeholder}
          errorTextId={errorTextId}
          helperTextId={helperTextId}
          charsExceededId={charsExceededId}
        />
      </TextFieldFormControl>

      <TextFieldFooter
        errorText={errorText}
        charsRemainingText={charsRemainingText}
        charsExceededText={charsExceededText}
        maxLength={maxLength}
        characterCount={characterCount}
        helperText={helperText}
        id={helperTextId}
        errorTextId={errorTextId}
        charsExceededId={charsExceededId}
        charsExceeded={charsExceeded}
      />
    </TextFieldBase>
  );
};

export default TextFieldMultiLine;
